import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "../../../../../../shell/hooks/useParams";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Typography from "@mui/material/Typography";
import ListItemIcon from "@mui/material/ListItemIcon";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseRounded from "@mui/icons-material/CloseRounded";
import AudiotrackRounded from "@mui/icons-material/AudiotrackRounded";
import PicutreasPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import DescriptionRounded from "@mui/icons-material/DescriptionRounded";
import SlideshowRounded from "@mui/icons-material/SlideshowRounded";
import BorderAllRounded from "@mui/icons-material/BorderAllRounded";
import CodeRounded from "@mui/icons-material/CodeRounded";
import FontDownloadRounded from "@mui/icons-material/FontDownloadRounded";
import FolderRounded from "@mui/icons-material/FolderRounded";
import FolderZipRounded from "@mui/icons-material/FolderZipRounded";
import CheckIcon from "@mui/icons-material/Check";
import { AppState } from "../../../../../../shell/store/types";
import { Filetype } from "../../../../../../shell/store/media-revamp";
import { ImageFilterRow } from "./ImageFilterRow";
import { VideoFilterRow } from "./VideoFilterRow";
import { FilterButton } from "../../../../../../shell/components/Filters";

// Localized label key per filetype *category* (matches the menu items). Specific
// format values (PNG, MP4, …) aren't here — they render as-is (raw value), since
// format identifiers aren't translated.
const FILETYPE_LABEL_KEYS: Partial<Record<Filetype, string>> = {
  Image: "media.filetypeFilterImages",
  Video: "media.filetypeFilterVideos",
  Audio: "media.filetypeFilterAudio",
  PDF: "media.filetypeFilterPdfs",
  Document: "media.filetypeFilterDocuments",
  Presentation: "media.filetypeFilterPresentations",
  Spreadsheet: "media.filetypeFilterSpreadsheets",
  Code: "common.code",
  Font: "media.filetypeFilterFonts",
  Folder: "media.filetypeFilterFolders",
  Archive: "media.filetypeFilterArchives"
};
export const FiletypeFilter: FC = () => {
  const {
    t
  } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [params, setParams] = useParams();
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const activeFilter = params.get("filetype") as Filetype | null;
  const handleChange = (filetype: Filetype) => {
    setParams(filetype, "filetype");
    handleClose();
  };
  return <>
      <FilterButton filterId="fileType" isFilterActive={!!activeFilter} buttonText={activeFilter ? FILETYPE_LABEL_KEYS[activeFilter] ? t(FILETYPE_LABEL_KEYS[activeFilter]) : activeFilter : t("media.filetypeFilterButtonLabel")} onOpenMenu={handleClick} onRemoveFilter={() => handleChange(null)} data-sentry-element="FilterButton" data-sentry-source-file="FiletypeFilter.tsx" />
      <Menu open={open} onClose={handleClose} anchorEl={anchorEl} data-sentry-element="Menu" data-sentry-source-file="FiletypeFilter.tsx">
        <ImageFilterRow onClose={handleClose} data-sentry-element="ImageFilterRow" data-sentry-source-file="FiletypeFilter.tsx" />
        <VideoFilterRow onClose={handleClose} data-sentry-element="VideoFilterRow" data-sentry-source-file="FiletypeFilter.tsx" />
        <MenuItem onClick={() => handleChange("Audio")} data-sentry-element="MenuItem" data-sentry-source-file="FiletypeFilter.tsx">
          <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FiletypeFilter.tsx">
            <AudiotrackRounded fontSize="small" data-sentry-element="AudiotrackRounded" data-sentry-source-file="FiletypeFilter.tsx" />
          </ListItemIcon>
          <Typography variant="body1" data-sentry-element="Typography" data-sentry-source-file="FiletypeFilter.tsx">
            {t("media.filetypeFilterAudio")}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => handleChange("PDF")} data-sentry-element="MenuItem" data-sentry-source-file="FiletypeFilter.tsx">
          <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FiletypeFilter.tsx">
            <PicutreasPdfRounded fontSize="small" data-sentry-element="PicutreasPdfRounded" data-sentry-source-file="FiletypeFilter.tsx" />
          </ListItemIcon>
          <Typography variant="body1" data-sentry-element="Typography" data-sentry-source-file="FiletypeFilter.tsx">
            {t("media.filetypeFilterPdfs")}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => handleChange("Document")} data-sentry-element="MenuItem" data-sentry-source-file="FiletypeFilter.tsx">
          <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FiletypeFilter.tsx">
            <DescriptionRounded fontSize="small" data-sentry-element="DescriptionRounded" data-sentry-source-file="FiletypeFilter.tsx" />
          </ListItemIcon>
          <Typography variant="body1" data-sentry-element="Typography" data-sentry-source-file="FiletypeFilter.tsx">
            {t("media.filetypeFilterDocuments")}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => handleChange("Presentation")} data-sentry-element="MenuItem" data-sentry-source-file="FiletypeFilter.tsx">
          <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FiletypeFilter.tsx">
            <SlideshowRounded fontSize="small" data-sentry-element="SlideshowRounded" data-sentry-source-file="FiletypeFilter.tsx" />
          </ListItemIcon>
          <Typography variant="body1" data-sentry-element="Typography" data-sentry-source-file="FiletypeFilter.tsx">
            {t("media.filetypeFilterPresentations")}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => handleChange("Spreadsheet")} data-sentry-element="MenuItem" data-sentry-source-file="FiletypeFilter.tsx">
          <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FiletypeFilter.tsx">
            <BorderAllRounded fontSize="small" data-sentry-element="BorderAllRounded" data-sentry-source-file="FiletypeFilter.tsx" />
          </ListItemIcon>
          <Typography variant="body1" data-sentry-element="Typography" data-sentry-source-file="FiletypeFilter.tsx">
            {t("media.filetypeFilterSpreadsheets")}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => handleChange("Code")} data-sentry-element="MenuItem" data-sentry-source-file="FiletypeFilter.tsx">
          <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FiletypeFilter.tsx">
            <CodeRounded fontSize="small" data-sentry-element="CodeRounded" data-sentry-source-file="FiletypeFilter.tsx" />
          </ListItemIcon>
          <Typography variant="body1" data-sentry-element="Typography" data-sentry-source-file="FiletypeFilter.tsx">{t("common.code")}</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleChange("Font")} data-sentry-element="MenuItem" data-sentry-source-file="FiletypeFilter.tsx">
          <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FiletypeFilter.tsx">
            <FontDownloadRounded fontSize="small" data-sentry-element="FontDownloadRounded" data-sentry-source-file="FiletypeFilter.tsx" />
          </ListItemIcon>
          <Typography variant="body1" data-sentry-element="Typography" data-sentry-source-file="FiletypeFilter.tsx">
            {t("media.filetypeFilterFonts")}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => handleChange("Folder")} data-sentry-element="MenuItem" data-sentry-source-file="FiletypeFilter.tsx">
          <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FiletypeFilter.tsx">
            <FolderRounded fontSize="small" data-sentry-element="FolderRounded" data-sentry-source-file="FiletypeFilter.tsx" />
          </ListItemIcon>
          <Typography variant="body1" data-sentry-element="Typography" data-sentry-source-file="FiletypeFilter.tsx">
            {t("media.filetypeFilterFolders")}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => handleChange("Archive")} data-sentry-element="MenuItem" data-sentry-source-file="FiletypeFilter.tsx">
          <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="FiletypeFilter.tsx">
            <FolderZipRounded fontSize="small" data-sentry-element="FolderZipRounded" data-sentry-source-file="FiletypeFilter.tsx" />
          </ListItemIcon>
          <Typography variant="body1" data-sentry-element="Typography" data-sentry-source-file="FiletypeFilter.tsx">
            {t("media.filetypeFilterArchives")}
          </Typography>
        </MenuItem>
      </Menu>
    </>;
};